import type { CompletedActivityMetricName, CompletedActivityRecord } from "./completedActivityHistory";

export type ComparableRideCategory =
  | "recovery_rides"
  | "endurance_rides"
  | "tempo_rides"
  | "sweet_spot_rides"
  | "threshold_workouts"
  | "vo2_workouts"
  | "races"
  | "mixed_group_rides"
  | "unknown";

export type ComparableRuleSet = {
  name: string;
  minimumScore: number;
  durationTolerancePercent: number;
  intensityFactorTolerance: number;
  elevationTolerancePercent: number;
  allowIndoorOutdoorMix: boolean;
  allowSurfaceMix: boolean;
  requireRespiratoryCoverageForRespiratoryMetrics: boolean;
  minimumActivityCompletenessPercent: number;
};

export type ComparabilityScoreComponents = {
  classificationMatch: number;
  durationSimilarity: number;
  intensitySimilarity: number;
  terrainSimilarity: number;
  activityContextMatch: number;
  dataCoverageCompatibility: number;
  sourceCompatibility: number;
};

export type ComparableRideMatch = {
  activityId: string;
  score: number;
  matchedRules: string[];
  mismatchReasons: string[];
  included: boolean;
  rank: number;
  scoreComponents: ComparabilityScoreComponents;
};

export type BaselineWindow = "last_3" | "last_7" | "last_28_days" | "last_90_days" | "all";
export type TrendDirection = "improving" | "declining" | "stable" | "insufficient_data";
export type TrendStrength = "weak" | "moderate" | "strong" | "unavailable";

export type BaselineMetric = {
  metricName: CompletedActivityMetricName;
  sampleCount: number;
  mean: number;
  median: number;
  standardDeviation: number;
  minimum: number;
  maximum: number;
  recentValue: number;
  percentile: number;
  percentDifference: number;
  absoluteDifference: number;
  trendDirection: TrendDirection;
  trendStrength: TrendStrength;
  confidence: "low" | "medium" | "high";
  window: BaselineWindow;
  comparableActivityIds: string[];
};

export type BaselineComparisonItem = {
  metricName: CompletedActivityMetricName;
  currentValue: number;
  personalBaseline: number;
  difference: number;
  percentDifference: number;
  percentile: number;
  trend: { direction: TrendDirection; strength: TrendStrength };
  interpretation: string;
  confidence: "low" | "medium" | "high";
  comparableRideCount: number;
  window: BaselineWindow;
};

export type PersonalBaselineResult = {
  comparableRideCount: number;
  comparisonWindow: BaselineWindow | null;
  baselineConfidence: "low" | "medium" | "high";
  insufficientHistory: boolean;
  topComparisons: BaselineComparisonItem[];
  allComparisons: BaselineComparisonItem[];
  comparableRides: Array<{ activityId: string; activityDate: string; score: number }>;
  diagnostics: {
    comparableCandidatesConsidered: number;
    inclusion: Array<{ activityId: string; included: boolean; score: number; reasons: string[] }>;
    selectedWindow: BaselineWindow | null;
    validSampleCounts: Array<{ metricName: CompletedActivityMetricName; sampleCount: number }>;
    trendMethod: string;
    confidenceReason: string;
  };
};

export const DEFAULT_COMPARABLE_RULES: Record<ComparableRideCategory, ComparableRuleSet> = {
  recovery_rides: {
    name: "Recovery ride compatibility",
    minimumScore: 70,
    durationTolerancePercent: 0.3,
    intensityFactorTolerance: 0.08,
    elevationTolerancePercent: 0.7,
    allowIndoorOutdoorMix: false,
    allowSurfaceMix: true,
    requireRespiratoryCoverageForRespiratoryMetrics: true,
    minimumActivityCompletenessPercent: 55
  },
  endurance_rides: {
    name: "Endurance ride compatibility",
    minimumScore: 68,
    durationTolerancePercent: 0.25,
    intensityFactorTolerance: 0.07,
    elevationTolerancePercent: 0.55,
    allowIndoorOutdoorMix: false,
    allowSurfaceMix: true,
    requireRespiratoryCoverageForRespiratoryMetrics: true,
    minimumActivityCompletenessPercent: 60
  },
  tempo_rides: {
    name: "Tempo ride compatibility",
    minimumScore: 70,
    durationTolerancePercent: 0.22,
    intensityFactorTolerance: 0.06,
    elevationTolerancePercent: 0.5,
    allowIndoorOutdoorMix: false,
    allowSurfaceMix: true,
    requireRespiratoryCoverageForRespiratoryMetrics: true,
    minimumActivityCompletenessPercent: 60
  },
  sweet_spot_rides: {
    name: "Sweet-spot workout compatibility",
    minimumScore: 72,
    durationTolerancePercent: 0.2,
    intensityFactorTolerance: 0.05,
    elevationTolerancePercent: 0.45,
    allowIndoorOutdoorMix: false,
    allowSurfaceMix: false,
    requireRespiratoryCoverageForRespiratoryMetrics: true,
    minimumActivityCompletenessPercent: 62
  },
  threshold_workouts: {
    name: "Threshold workout compatibility",
    minimumScore: 74,
    durationTolerancePercent: 0.2,
    intensityFactorTolerance: 0.05,
    elevationTolerancePercent: 0.45,
    allowIndoorOutdoorMix: false,
    allowSurfaceMix: false,
    requireRespiratoryCoverageForRespiratoryMetrics: true,
    minimumActivityCompletenessPercent: 62
  },
  vo2_workouts: {
    name: "VO2 workout compatibility",
    minimumScore: 75,
    durationTolerancePercent: 0.22,
    intensityFactorTolerance: 0.06,
    elevationTolerancePercent: 0.5,
    allowIndoorOutdoorMix: false,
    allowSurfaceMix: false,
    requireRespiratoryCoverageForRespiratoryMetrics: true,
    minimumActivityCompletenessPercent: 62
  },
  races: {
    name: "Race compatibility",
    minimumScore: 76,
    durationTolerancePercent: 0.3,
    intensityFactorTolerance: 0.09,
    elevationTolerancePercent: 0.7,
    allowIndoorOutdoorMix: false,
    allowSurfaceMix: false,
    requireRespiratoryCoverageForRespiratoryMetrics: true,
    minimumActivityCompletenessPercent: 60
  },
  mixed_group_rides: {
    name: "Mixed/group ride compatibility",
    minimumScore: 66,
    durationTolerancePercent: 0.35,
    intensityFactorTolerance: 0.1,
    elevationTolerancePercent: 0.8,
    allowIndoorOutdoorMix: false,
    allowSurfaceMix: true,
    requireRespiratoryCoverageForRespiratoryMetrics: true,
    minimumActivityCompletenessPercent: 55
  },
  unknown: {
    name: "Unknown ride compatibility",
    minimumScore: 78,
    durationTolerancePercent: 0.15,
    intensityFactorTolerance: 0.04,
    elevationTolerancePercent: 0.3,
    allowIndoorOutdoorMix: false,
    allowSurfaceMix: false,
    requireRespiratoryCoverageForRespiratoryMetrics: true,
    minimumActivityCompletenessPercent: 65
  }
};

const METRIC_DESIRABILITY: Record<CompletedActivityMetricName, "higher" | "lower" | "neutral"> = {
  variability_index: "lower",
  aerobic_decoupling: "lower",
  normalized_power: "neutral",
  intensity_factor: "neutral",
  first_half_vs_second_half_power: "lower",
  durability60: "lower",
  durability90: "lower",
  durability120: "lower",
  cadence_stability: "higher",
  average_minute_ventilation: "neutral",
  maximum_minute_ventilation: "neutral",
  average_breathing_frequency: "neutral",
  respiratory_drift: "lower",
  ventilation_to_power_relationship: "neutral",
  respiratory_coverage: "higher"
};

const METRIC_ORDER: CompletedActivityMetricName[] = [
  "variability_index",
  "aerobic_decoupling",
  "normalized_power",
  "intensity_factor",
  "first_half_vs_second_half_power",
  "durability60",
  "durability90",
  "durability120",
  "cadence_stability",
  "average_minute_ventilation",
  "maximum_minute_ventilation",
  "average_breathing_frequency",
  "respiratory_drift",
  "ventilation_to_power_relationship",
  "respiratory_coverage"
];

function categoryForRecord(record: CompletedActivityRecord): ComparableRideCategory {
  const type = record.rideClassification.type;
  if (type === "recovery") return "recovery_rides";
  if (type === "endurance") return "endurance_rides";
  if (type === "tempo") return "tempo_rides";
  if (type === "sweet_spot") return "sweet_spot_rides";
  if (type === "threshold") return "threshold_workouts";
  if (type === "vo2") return "vo2_workouts";
  if (type === "race") return "races";
  if (type === "group_ride" || type === "mixed") return "mixed_group_rides";
  return "unknown";
}

function ratioDifference(left: number | null, right: number | null): number | null {
  if (left === null || right === null || Math.max(Math.abs(left), Math.abs(right)) === 0) return null;
  return Math.abs(left - right) / Math.max(Math.abs(left), Math.abs(right));
}

function linearSimilarity(diff: number, tolerance: number): number {
  if (!Number.isFinite(diff) || !Number.isFinite(tolerance) || tolerance <= 0) return 0;
  return Math.max(0, 100 * (1 - diff / tolerance));
}

function computeComponents(
  current: CompletedActivityRecord,
  candidate: CompletedActivityRecord,
  rules: ComparableRuleSet
): { components: ComparabilityScoreComponents; matchedRules: string[]; mismatchReasons: string[] } {
  const matchedRules: string[] = [];
  const mismatchReasons: string[] = [];

  let classificationMatch = 0;
  if (current.rideClassification.type === candidate.rideClassification.type) {
    classificationMatch = 100;
    matchedRules.push("same_primary_classification");
  } else if (categoryForRecord(current) === categoryForRecord(candidate)) {
    classificationMatch = 70;
    matchedRules.push("same_comparable_category");
  } else {
    mismatchReasons.push("Ride classification differs");
  }

  const durationDiff = ratioDifference(current.durationSeconds, candidate.durationSeconds);
  const durationSimilarity = durationDiff === null ? 30 : linearSimilarity(durationDiff, rules.durationTolerancePercent);
  if (durationSimilarity >= 70) {
    matchedRules.push("duration_within_tolerance");
  } else {
    mismatchReasons.push("Duration outside tolerance");
  }

  const ifDiff = current.intensityFactor !== null && candidate.intensityFactor !== null
    ? Math.abs(current.intensityFactor - candidate.intensityFactor)
    : null;
  const intensitySimilarity = ifDiff === null ? 40 : linearSimilarity(ifDiff, rules.intensityFactorTolerance);
  if (intensitySimilarity >= 70) {
    matchedRules.push("intensity_within_tolerance");
  } else {
    mismatchReasons.push("Intensity factor differs");
  }

  const currentClimbRate = current.elevationGainMeters !== null && current.durationSeconds ? current.elevationGainMeters / (current.durationSeconds / 3600) : null;
  const candidateClimbRate = candidate.elevationGainMeters !== null && candidate.durationSeconds ? candidate.elevationGainMeters / (candidate.durationSeconds / 3600) : null;
  const elevationDiff = ratioDifference(currentClimbRate, candidateClimbRate);
  const terrainSimilarity = elevationDiff === null ? 55 : linearSimilarity(elevationDiff, rules.elevationTolerancePercent);
  if (terrainSimilarity >= 65) {
    matchedRules.push("terrain_profile_similar");
  }

  let activityContextMatch = 100;
  if (!rules.allowIndoorOutdoorMix && current.activityContext.indoorOutdoor !== "unknown" && candidate.activityContext.indoorOutdoor !== "unknown" && current.activityContext.indoorOutdoor !== candidate.activityContext.indoorOutdoor) {
    activityContextMatch -= 80;
    mismatchReasons.push("Indoor/outdoor mismatch");
  }
  if (!rules.allowSurfaceMix && current.activityContext.surface !== "unknown" && candidate.activityContext.surface !== "unknown" && current.activityContext.surface !== candidate.activityContext.surface) {
    activityContextMatch -= 55;
    mismatchReasons.push("Road/gravel mismatch");
  }
  activityContextMatch = Math.max(0, activityContextMatch);
  if (activityContextMatch >= 70) {
    matchedRules.push("activity_context_match");
  }

  let dataCoverageCompatibility = 100;
  if (candidate.sourceCoverage.activityCompletenessPercent < rules.minimumActivityCompletenessPercent) {
    dataCoverageCompatibility -= 55;
    mismatchReasons.push("Insufficient source coverage");
  }
  if (current.rideClassification.confidencePercent < 65 || candidate.rideClassification.confidencePercent < 65) {
    dataCoverageCompatibility -= 18;
    mismatchReasons.push("Ride classification uncertainty");
  }
  dataCoverageCompatibility = Math.max(0, dataCoverageCompatibility);
  if (dataCoverageCompatibility >= 70) {
    matchedRules.push("coverage_compatible");
  }

  let sourceCompatibility = 100;
  const ftpMissing = current.ftpSnapshot.ftpWatts === null || candidate.ftpSnapshot.ftpWatts === null;
  if (ftpMissing) {
    sourceCompatibility -= 40;
    mismatchReasons.push("FTP context missing");
  }
  const ftpDiff = current.ftpSnapshot.ftpWatts !== null && candidate.ftpSnapshot.ftpWatts !== null
    ? Math.abs(current.ftpSnapshot.ftpWatts - candidate.ftpSnapshot.ftpWatts) / Math.max(current.ftpSnapshot.ftpWatts, candidate.ftpSnapshot.ftpWatts)
    : null;
  if (ftpDiff !== null && ftpDiff > 0.1) {
    sourceCompatibility -= 35;
    mismatchReasons.push("FTP context inconsistent");
  }
  if (rules.requireRespiratoryCoverageForRespiratoryMetrics && (current.respiratorySummary.available !== candidate.respiratorySummary.available)) {
    sourceCompatibility -= 18;
    mismatchReasons.push("Respiratory coverage mismatch");
  }
  sourceCompatibility = Math.max(0, sourceCompatibility);
  if (sourceCompatibility >= 70) {
    matchedRules.push("source_compatibility");
  }

  return {
    components: {
      classificationMatch,
      durationSimilarity,
      intensitySimilarity,
      terrainSimilarity,
      activityContextMatch,
      dataCoverageCompatibility,
      sourceCompatibility
    },
    matchedRules,
    mismatchReasons
  };
}

function scoreComponents(components: ComparabilityScoreComponents): number {
  const weighted =
    components.classificationMatch * 0.24
    + components.durationSimilarity * 0.16
    + components.intensitySimilarity * 0.17
    + components.terrainSimilarity * 0.1
    + components.activityContextMatch * 0.14
    + components.dataCoverageCompatibility * 0.1
    + components.sourceCompatibility * 0.09;
  return Math.round(Math.max(0, Math.min(100, weighted)));
}

export function selectComparableRides(
  current: CompletedActivityRecord,
  history: CompletedActivityRecord[],
  rulesByCategory: Record<ComparableRideCategory, ComparableRuleSet> = DEFAULT_COMPARABLE_RULES
): ComparableRideMatch[] {
  const category = categoryForRecord(current);
  const rules = rulesByCategory[category] ?? rulesByCategory.unknown;

  const matches = history
    .filter(record => record.activityId !== current.activityId)
    .map(record => {
      const evaluation = computeComponents(current, record, rules);
      const score = scoreComponents(evaluation.components);
      const included = score >= rules.minimumScore
        && evaluation.components.classificationMatch >= 70
        && evaluation.components.activityContextMatch >= 40
        && evaluation.components.dataCoverageCompatibility >= 45
        && evaluation.components.sourceCompatibility >= 40;
      return {
        activityId: record.activityId,
        score,
        matchedRules: [rules.name, ...evaluation.matchedRules],
        mismatchReasons: evaluation.mismatchReasons,
        included,
        rank: 0,
        scoreComponents: evaluation.components
      } satisfies ComparableRideMatch;
    })
    .sort((left, right) => right.score - left.score || left.activityId.localeCompare(right.activityId));

  return matches.map((match, index) => ({ ...match, rank: index + 1 }));
}

function extractMetricValue(record: CompletedActivityRecord, metricName: CompletedActivityMetricName): number | null {
  switch (metricName) {
    case "variability_index":
      return record.variabilityIndex;
    case "aerobic_decoupling":
      return record.aerobicDecoupling;
    case "normalized_power":
      return record.normalizedPower;
    case "intensity_factor":
      return record.intensityFactor;
    case "first_half_vs_second_half_power":
      if (record.firstHalfPower === null || record.secondHalfPower === null || record.firstHalfPower === 0) return null;
      return ((record.secondHalfPower - record.firstHalfPower) / record.firstHalfPower) * 100;
    case "durability60":
      return record.durability60;
    case "durability90":
      return record.durability90;
    case "durability120":
      return record.durability120;
    case "cadence_stability":
      return record.cadenceStability;
    case "average_minute_ventilation":
      return record.respiratorySummary.averageMinuteVentilation;
    case "maximum_minute_ventilation":
      return record.respiratorySummary.maximumMinuteVentilation;
    case "average_breathing_frequency":
      return record.respiratorySummary.averageBreathingFrequency;
    case "respiratory_drift":
      return record.respiratorySummary.respiratoryDriftPercent;
    case "ventilation_to_power_relationship":
      return record.respiratorySummary.ventilationToPowerRelationship;
    case "respiratory_coverage":
      return record.respiratorySummary.coveragePercent;
    default:
      return null;
  }
}

function valuesMean(values: number[]): number {
  return values.reduce((sum, value) => sum + value, 0) / values.length;
}

function valuesMedian(values: number[]): number {
  const sorted = [...values].sort((left, right) => left - right);
  const middle = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0
    ? (sorted[middle - 1] + sorted[middle]) / 2
    : sorted[middle];
}

function valuesStdDev(values: number[]): number {
  if (values.length <= 1) return 0;
  const mean = valuesMean(values);
  const variance = values.reduce((sum, value) => sum + (value - mean) ** 2, 0) / values.length;
  return Math.sqrt(Math.max(variance, 0));
}

function toConfidence(value: number): "low" | "medium" | "high" {
  if (value >= 78) return "high";
  if (value >= 58) return "medium";
  return "low";
}

function trendFromSeries(
  valuesInTimeOrder: number[],
  metricName: CompletedActivityMetricName
): { direction: TrendDirection; strength: TrendStrength } {
  if (valuesInTimeOrder.length < 3) {
    return { direction: "insufficient_data", strength: "unavailable" };
  }

  const n = valuesInTimeOrder.length;
  const xs = valuesInTimeOrder.map((_value, index) => index + 1);
  const meanX = valuesMean(xs);
  const meanY = valuesMean(valuesInTimeOrder);
  let numerator = 0;
  let denominator = 0;
  for (let index = 0; index < n; index += 1) {
    numerator += (xs[index] - meanX) * (valuesInTimeOrder[index] - meanY);
    denominator += (xs[index] - meanX) ** 2;
  }
  const slope = denominator === 0 ? 0 : numerator / denominator;
  const std = valuesStdDev(valuesInTimeOrder);
  const normalizedSlope = std === 0 ? 0 : Math.abs(slope) / std;

  const strength: TrendStrength = normalizedSlope >= 0.5
    ? "strong"
    : normalizedSlope >= 0.25
      ? "moderate"
      : normalizedSlope > 0.08
        ? "weak"
        : "weak";

  const desirability = METRIC_DESIRABILITY[metricName];
  if (std === 0 || Math.abs(slope) <= std * 0.05) {
    return { direction: "stable", strength: "weak" };
  }

  if (desirability === "neutral") {
    return { direction: "stable", strength };
  }

  const increasing = slope > 0;
  if (desirability === "higher") {
    return { direction: increasing ? "improving" : "declining", strength };
  }

  return { direction: increasing ? "declining" : "improving", strength };
}

function getWindowRecords(records: CompletedActivityRecord[], now: number, window: BaselineWindow): CompletedActivityRecord[] {
  const sorted = [...records].sort((left, right) => Date.parse(right.activityDate) - Date.parse(left.activityDate));
  if (window === "all") return sorted;
  if (window === "last_3") return sorted.slice(0, 3);
  if (window === "last_7") return sorted.slice(0, 7);

  const maxDays = window === "last_28_days" ? 28 : 90;
  return sorted.filter(record => {
    const ageMs = Math.max(0, now - Date.parse(record.activityDate));
    return ageMs <= maxDays * 24 * 60 * 60 * 1000;
  });
}

function chooseComparisonWindow(count: number): BaselineWindow | null {
  if (count < 3) return null;
  if (count >= 7) return "last_7";
  if (count >= 3) return "last_3";
  return "all";
}

function interpretationForMetric(metricName: CompletedActivityMetricName, comparison: BaselineComparisonItem): string {
  const lowerIsBetter = METRIC_DESIRABILITY[metricName] === "lower";
  const higherIsBetter = METRIC_DESIRABILITY[metricName] === "higher";
  if (metricName === "variability_index" && comparison.currentValue <= comparison.personalBaseline) {
    return "among your most stable comparable endurance rides";
  }
  const withinTypical = Math.abs(comparison.percentDifference) <= 4.5;
  if (withinTypical) {
    return "typical for your comparable rides";
  }

  if (comparison.percentDifference < 0) {
    if (lowerIsBetter) return "lower than your recent comparable range";
    if (higherIsBetter) return "lower than your recent comparable range";
    return "lower than your recent comparable range";
  }

  return "higher than your recent comparable range";
}

function buildMetricComparison(
  metricName: CompletedActivityMetricName,
  currentRecord: CompletedActivityRecord,
  comparableRecords: CompletedActivityRecord[],
  now: number,
  selectedWindow: BaselineWindow
): { baselineMetric: BaselineMetric | null; comparison: BaselineComparisonItem | null } {
  const currentValue = extractMetricValue(currentRecord, metricName);
  if (currentValue === null) {
    return { baselineMetric: null, comparison: null };
  }

  const windowOrder: BaselineWindow[] = selectedWindow === "last_7"
    ? ["last_7", "last_28_days", "last_90_days", "all"]
    : selectedWindow === "last_3"
      ? ["last_3", "last_7", "last_28_days", "all"]
      : [selectedWindow, "all"];

  for (const window of windowOrder) {
    const scoped = getWindowRecords(comparableRecords, now, window);
    const values = scoped
      .map(record => ({ id: record.activityId, value: extractMetricValue(record, metricName), date: Date.parse(record.activityDate) }))
      .filter((item): item is { id: string; value: number; date: number } => item.value !== null)
      .sort((left, right) => left.date - right.date);

    if (values.length < 3) {
      continue;
    }

    const valueList = values.map(item => item.value);
    const mean = valuesMean(valueList);
    const median = valuesMedian(valueList);
    const std = valuesStdDev(valueList);
    const min = Math.min(...valueList);
    const max = Math.max(...valueList);
    const absoluteDifference = currentValue - median;
    const percentDifference = median === 0 ? 0 : (absoluteDifference / Math.abs(median)) * 100;
    const lessOrEqualCount = valueList.filter(value => value <= currentValue).length;
    const percentile = Math.round((lessOrEqualCount / valueList.length) * 100);
    const trend = trendFromSeries(valueList, metricName);

    const confidenceBase = Math.min(95, 35 + values.length * 8 + Math.max(0, 25 - std));
    const confidence = toConfidence(confidenceBase);

    const baselineMetric: BaselineMetric = {
      metricName,
      sampleCount: values.length,
      mean,
      median,
      standardDeviation: std,
      minimum: min,
      maximum: max,
      recentValue: currentValue,
      percentile,
      percentDifference,
      absoluteDifference,
      trendDirection: trend.direction,
      trendStrength: trend.strength,
      confidence,
      window,
      comparableActivityIds: values.map(item => item.id)
    };

    const comparison: BaselineComparisonItem = {
      metricName,
      currentValue,
      personalBaseline: median,
      difference: absoluteDifference,
      percentDifference,
      percentile,
      trend,
      interpretation: "",
      confidence,
      comparableRideCount: values.length,
      window
    };
    comparison.interpretation = interpretationForMetric(metricName, comparison);

    return { baselineMetric, comparison };
  }

  return { baselineMetric: null, comparison: null };
}

export function buildPersonalBaselineResult(
  currentRecord: CompletedActivityRecord,
  historyRecords: CompletedActivityRecord[]
): PersonalBaselineResult {
  const comparableCandidates = selectComparableRides(currentRecord, historyRecords);
  const includedComparables = comparableCandidates.filter(match => match.included);
  const comparableById = new Map(historyRecords.map(record => [record.activityId, record]));
  const comparableRecords = includedComparables
    .map(match => comparableById.get(match.activityId))
    .filter((record): record is CompletedActivityRecord => Boolean(record));
  const comparableRideCount = comparableRecords.length;
  const comparisonWindow = chooseComparisonWindow(comparableRideCount);
  const now = Date.now();

  if (comparisonWindow === null) {
    return {
      comparableRideCount,
      comparisonWindow: null,
      baselineConfidence: "low",
      insufficientHistory: true,
      topComparisons: [],
      allComparisons: [],
      comparableRides: includedComparables.map(match => ({
        activityId: match.activityId,
        activityDate: comparableById.get(match.activityId)?.activityDate ?? new Date().toISOString(),
        score: match.score
      })),
      diagnostics: {
        comparableCandidatesConsidered: comparableCandidates.length,
        inclusion: comparableCandidates.map(match => ({
          activityId: match.activityId,
          included: match.included,
          score: match.score,
          reasons: [...match.matchedRules, ...match.mismatchReasons]
        })),
        selectedWindow: null,
        validSampleCounts: [],
        trendMethod: "linear_regression_slope_vs_variability",
        confidenceReason: "Fewer than three comparable rides"
      }
    };
  }

  const baselineComparisons: BaselineComparisonItem[] = [];
  const validSampleCounts: Array<{ metricName: CompletedActivityMetricName; sampleCount: number }> = [];

  for (const metricName of METRIC_ORDER) {
    if (metricName.startsWith("respiratory") && !currentRecord.respiratorySummary.available) {
      continue;
    }
    if ((metricName === "average_minute_ventilation" || metricName === "maximum_minute_ventilation" || metricName === "average_breathing_frequency" || metricName === "respiratory_drift" || metricName === "ventilation_to_power_relationship" || metricName === "respiratory_coverage")
      && !comparableRecords.every(record => record.respiratorySummary.available)) {
      continue;
    }

    const built = buildMetricComparison(metricName, currentRecord, comparableRecords, now, comparisonWindow);
    if (built.baselineMetric && built.comparison) {
      validSampleCounts.push({ metricName, sampleCount: built.baselineMetric.sampleCount });
      baselineComparisons.push(built.comparison);
    }
  }

  const confidenceNumerator = baselineComparisons.reduce((sum, item) => sum + (item.confidence === "high" ? 3 : item.confidence === "medium" ? 2 : 1), 0);
  const confidenceDenominator = Math.max(1, baselineComparisons.length * 3);
  const baselineConfidence = toConfidence((confidenceNumerator / confidenceDenominator) * 100);

  const prioritized = [...baselineComparisons].sort((left, right) => {
    const leftSignal = Math.abs(left.percentile - 50) + Math.abs(left.difference);
    const rightSignal = Math.abs(right.percentile - 50) + Math.abs(right.difference);
    return rightSignal - leftSignal;
  });

  return {
    comparableRideCount,
    comparisonWindow,
    baselineConfidence,
    insufficientHistory: false,
    topComparisons: prioritized.slice(0, 5),
    allComparisons: prioritized,
    comparableRides: includedComparables.map(match => ({
      activityId: match.activityId,
      activityDate: comparableById.get(match.activityId)?.activityDate ?? new Date().toISOString(),
      score: match.score
    })),
    diagnostics: {
      comparableCandidatesConsidered: comparableCandidates.length,
      inclusion: comparableCandidates.map(match => ({
        activityId: match.activityId,
        included: match.included,
        score: match.score,
        reasons: [...match.matchedRules, ...match.mismatchReasons]
      })),
      selectedWindow: comparisonWindow,
      validSampleCounts,
      trendMethod: "linear_regression_slope_vs_variability",
      confidenceReason: `Comparable rides ${comparableRideCount}; usable metrics ${baselineComparisons.length}`
    }
  };
}
